import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ENVIRONMENT_TOKEN } from '@aksia/infrastructure';
import { ToasterService } from '@aksia/ui';
import { map } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { isValidReturnUrl } from '../../utils/return-url.utils';
import {
  AuthenticatorSetup,
  SecondFactorService,
} from '../../services/second-factor.service';
import { QrCodeComponent } from '../../components/qr-code/qr-code.component';

@Component({
  selector: 'app-setup-authenticator',
  imports: [QrCodeComponent],
  templateUrl: './setup-authenticator.content.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetupAuthenticatorPageContent implements OnInit {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly toaster = inject(ToasterService);
  private readonly secondFactor = inject(SecondFactorService);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly setup = signal<AuthenticatorSetup | null>(null);
  protected readonly code = signal('');
  protected readonly emailCode = signal('');
  protected readonly rememberDevice = signal(false);
  protected readonly inFlight = signal(false);
  protected readonly attemptedSubmit = signal(false);
  protected readonly recoveryCodes = signal<string[] | null>(null);

  protected readonly returnUrl = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('returnUrl') ?? undefined)),
    { initialValue: undefined as string | undefined },
  );

  protected readonly returnUrlValid = computed(() =>
    isValidReturnUrl(this.returnUrl()),
  );

  protected readonly authenticatorLink = computed<SafeUrl | null>(() => {
    const uri = this.setup()?.authenticatorUri;
    return uri ? this.sanitizer.bypassSecurityTrustUrl(uri) : null;
  });

  @HostBinding('class')
  protected readonly hostClass = 'auth-page';

  ngOnInit() {
    if (!this.returnUrlValid()) return;

    this.secondFactor.setupAuthenticator().subscribe({
      next: (setup) => this.setup.set(setup),
      error: (err: HttpErrorResponse) => this.handleError(err),
    });
  }

  protected updateCode(value: string) {
    this.code.set(value.replace(/\D/g, '').slice(0, 6));
  }

  protected updateEmailCode(value: string) {
    this.emailCode.set(value.replace(/\D/g, '').slice(0, 8));
  }

  protected submit(event?: Event) {
    event?.preventDefault();
    this.attemptedSubmit.set(true);

    const needsEmailCode = !!this.setup()?.emailCodeSentTo;
    if (!this.code() || (needsEmailCode && !this.emailCode())) return;

    this.inFlight.set(true);
    this.secondFactor
      .confirmAuthenticator(
        this.code(),
        this.emailCode(),
        this.rememberDevice(),
      )
      .subscribe({
        next: (res) => {
          this.inFlight.set(false);
          this.recoveryCodes.set(res.recoveryCodes ?? []);
        },
        error: (err: HttpErrorResponse) => {
          this.inFlight.set(false);
          this.handleError(err);
        },
      });
  }

  protected continueToApp() {
    window.location.href =
      this.environment.microservices_api_url + this.returnUrl();
  }

  protected goToLogin() {
    const returnUrl = this.returnUrl();
    this.router.navigate(
      ['/'],
      returnUrl ? { queryParams: { returnUrl } } : {},
    );
  }

  private handleError(err: HttpErrorResponse) {
    if (err.status === 429) {
      this.toaster.error(
        'Too many attempts. Please wait a moment and try again.',
      );
      return;
    }

    if (err.status === 401) {
      this.goToLogin();
      return;
    }

    const description = err.error?.error_description as string | undefined;
    switch (err.error?.error as string | undefined) {
      case 'code_incorrect':
        this.toaster.error(
          description ?? 'The code is incorrect. Please try again.',
        );
        break;
      case 'code_expired':
      case 'code_blocked':
        this.toaster.error(description ?? 'Please sign in again.');
        this.goToLogin();
        break;
      default:
        this.toaster.error(
          description ?? 'Something went wrong. Please try again.',
        );
    }
  }
}
